import { type ColumnFiltersState } from '@apps/web/lib/table';
import {
    type EntityFilterDefaultsRead,
    type ShiftFilterOptionsRead,
} from '@libraries/typescript/api_client/schema_types';

// Taxonomy-A column filters the v2 server applies. `columnId` is the table's
// tanstack column id; `key` is the API column_filters key (record attribute,
// which differs for tags); `defaultIncludeMode` matches the facet config so
// server semantics line up when the value omits an explicit mode.
//
// INVARIANT: the table runs with `manualFiltering`, so tanstack does no
// client-side row filtering. Every filterable toolbar column must therefore
// either appear here or have dedicated server handling (`star_rating` via
// min/max bounds, distance via `max_distance_mi`, contact attempts via
// `max_contact_attempts_48h`). A column missing from all of these silently
// stops filtering. `is_preferred` is the exception: it is compiled by the v2
// DSL builder, so it is intentionally absent here.
const SERVER_COLUMN_FILTERS: ReadonlyArray<{
    readonly columnId: string;
    readonly key: string;
    readonly valueKind: 'array' | 'single';
    readonly defaultIncludeMode: 'any' | 'all';
}> = [
    {
        columnId: 'gender',
        key: 'gender',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'caregiver_tags',
        key: 'tags',
        valueKind: 'array',
        defaultIncludeMode: 'all',
    },
    {
        columnId: 'primary_language',
        key: 'primary_language',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'roles',
        key: 'roles',
        valueKind: 'array',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'postal_code',
        key: 'postal_code',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'office_name',
        key: 'office_name',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'overtime_status',
        key: 'overtime_status',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'schedule_availability',
        key: 'schedule_availability',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
    {
        columnId: 'availability_preference',
        key: 'availability_preference',
        valueKind: 'single',
        defaultIncludeMode: 'any',
    },
];

const SERVER_COLUMN_FILTER_KEY_TO_CONFIG: ReadonlyMap<
    string,
    (typeof SERVER_COLUMN_FILTERS)[number]
> = new Map(SERVER_COLUMN_FILTERS.map((config) => [config.key, config]));

function normalizeServerIncludeMode(
    config: (typeof SERVER_COLUMN_FILTERS)[number],
    mode: 'any' | 'all' | undefined,
): 'any' | 'all' {
    if (config.valueKind === 'single') {
        return 'any';
    }
    return mode ?? config.defaultIncludeMode;
}

function normalizeServerExcludeMode(
    config: (typeof SERVER_COLUMN_FILTERS)[number],
    mode: 'any' | 'all' | undefined,
): 'any' | 'all' {
    if (config.valueKind === 'array' && mode === 'all') {
        return 'all';
    }
    return 'any';
}

const FACET_FIELD_BY_COLUMN_ID: ReadonlyMap<
    string,
    keyof ShiftFilterOptionsRead
> = new Map([
    ['caregiver_tags', 'caregiver_tags'],
    ['office_name', 'office_name'],
    ['postal_code', 'postal_code'],
    ['gender', 'gender'],
    ['primary_language', 'primary_language'],
    ['roles', 'roles'],
]);

export function buildEntityDefaultColumnFilters(
    defaults: EntityFilterDefaultsRead,
): ColumnFiltersState {
    const filters: ColumnFiltersState = [];
    for (const [key, value] of Object.entries(defaults.column_filters)) {
        const config = SERVER_COLUMN_FILTER_KEY_TO_CONFIG.get(key);
        if (!config) {
            continue;
        }
        const include = value.include ?? [];
        const exclude = value.exclude ?? [];
        if (include.length === 0 && exclude.length === 0) {
            continue;
        }
        filters.push({
            id: config.columnId,
            value: {
                include: [...include],
                exclude: [...exclude],
                includeMode: normalizeServerIncludeMode(
                    config,
                    value.include_mode,
                ),
                excludeMode:
                    exclude.length > 0
                        ? normalizeServerExcludeMode(config, value.exclude_mode)
                        : 'any',
            },
        });
    }
    // Scalar defaults the table represents as column filters.
    if (defaults.overtime_behavior === 'hide') {
        filters.push({
            id: 'overtime_status',
            value: { include: ['within_limit'], exclude: [] },
        });
    }
    if (!defaults.include_schedule_conflicts) {
        filters.push({
            id: 'schedule_availability',
            value: { include: [], exclude: ['has_shift_conflict'] },
        });
    }
    if (!defaults.include_preference_unavailable) {
        filters.push({
            id: 'availability_preference',
            value: { include: [], exclude: ['unavailable'] },
        });
    }
    if (defaults.min_star_rating !== null) {
        filters.push({
            id: 'star_rating',
            value: {
                operator: 'greater_than',
                value: defaults.min_star_rating - 1,
            },
        });
    }
    return filters;
}

export function reconcileColumnFiltersAgainstFacets(
    filters: ColumnFiltersState,
    options: ShiftFilterOptionsRead | undefined,
): ColumnFiltersState {
    if (!options) {
        return filters;
    }
    const reconciled: ColumnFiltersState = [];
    for (const filter of filters) {
        const facetField = FACET_FIELD_BY_COLUMN_ID.get(filter.id);
        if (!facetField) {
            reconciled.push(filter);
            continue;
        }
        const allowed = new Set(options[facetField] as Array<string>);
        const value = filter.value as
            | {
                  include?: Array<string>;
                  exclude?: Array<string>;
                  includeMode?: 'any' | 'all';
                  excludeMode?: 'any' | 'all';
              }
            | undefined;
        const include = (value?.include ?? []).filter((v) => allowed.has(v));
        const exclude = (value?.exclude ?? []).filter((v) => allowed.has(v));
        if (include.length === 0 && exclude.length === 0) {
            continue;
        }
        reconciled.push({
            id: filter.id,
            value: { ...value, include, exclude },
        });
    }
    return reconciled;
}
